import { useEffect, useState } from "react";
import { FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

const EI_STYLE = {
  delivered: "bg-emerald-50 text-emerald-700",
  accepted: "bg-emerald-50 text-emerald-700",
  sent: "bg-sky-50 text-sky-700",
  not_sent: "bg-slate-100 text-slate-500",
  rejected: "bg-red-50 text-red-600",
  error: "bg-red-50 text-red-600",
};

export default function AdminInvoices() {
  const { t, fmt } = useLanguage();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = (p = 1) => api.get(`/admin/fatture/invoices?page=${p}`).then(({ data }) => {
    setData(data);
    setPage(p);
    setHasMore(!!data.has_more);
  }).catch(() => setData({ data: [], connected: false }));

  useEffect(() => { load(1); }, []);

  const download = async (orderId, num) => {
    setBusyId(orderId);
    try {
      const { data } = await api.get(`/orders/${orderId}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `SIMAMMA-fattura-${num || orderId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("misc.error"));
    } finally {
      setBusyId(null);
    }
  };

  if (!data) {
    return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>;
  }

  const th = "text-left font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 px-5 py-3";
  const td = "px-5 py-4 text-sm text-slate-700 align-middle";

  return (
    <div data-testid="admin-invoices-page" className="mt-10">
      <h2 className="font-display font-bold text-2xl text-slate-900">{t("inv.title")}</h2>
      <p className="mt-1 text-sm text-slate-500 max-w-2xl">{t("inv.sub")}</p>

      <div className="mt-6 rounded-3xl bg-white border border-slate-200/80 shadow-sm overflow-x-auto">
        {!data.connected ? (
          <p data-testid="inv-not-connected" className="p-10 text-left text-sm text-slate-400">{t("inv.notConnected")}</p>
        ) : data.data.length === 0 ? (
          <p data-testid="inv-empty" className="p-10 text-left text-sm text-slate-400">{t("inv.empty")}</p>
        ) : (
          <table className="w-full min-w-[820px]" data-testid="inv-table">
            <thead className="border-b border-slate-100">
              <tr>
                <th className={th}>{t("inv.colNumber")}</th>
                <th className={th}>{t("inv.colDate")}</th>
                <th className={th}>{t("inv.colCustomer")}</th>
                <th className={th}>{t("inv.colAmount")}</th>
                <th className={th}>{t("inv.colSdi")}</th>
                <th className={th}>{t("console.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((d) => (
                <tr key={d.id} data-testid={`inv-row-${d.id}`} className="border-b border-slate-50 hover:bg-[#FAF5EE] transition-colors">
                  <td className={`${td} font-mono-t text-xs font-bold`}>{d.number}{d.numeration || ""}</td>
                  <td className={`${td} text-xs font-mono-t`}>{d.date}</td>
                  <td className={td}>
                    <div className="font-medium text-slate-800">{d.entity_name}</div>
                    {d.email && <div className="text-xs text-slate-400">{d.email}</div>}
                  </td>
                  <td className={`${td} font-semibold`}>{d.amount_gross != null ? fmt(d.amount_gross) : "—"}</td>
                  <td className={td}>
                    <span data-testid={`inv-sdi-${d.id}`} className={`rounded-full px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest ${EI_STYLE[d.ei_status] || EI_STYLE.not_sent}`}>
                      {d.ei_status || "not_sent"}
                    </span>
                  </td>
                  <td className={td}>
                    {d.order_id && (
                      <button data-testid={`inv-download-${d.id}`} disabled={busyId === d.order_id}
                        onClick={() => download(d.order_id, `${d.number}${d.numeration || ""}`)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors disabled:opacity-60">
                        {busyId === d.order_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                        PDF
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(page > 1 || hasMore) && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <button data-testid="inv-prev" disabled={page <= 1} onClick={() => load(page - 1)}
            className="rounded-full border border-slate-200 px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] disabled:opacity-40 transition-colors">←</button>
          <span className="font-mono-t text-xs text-slate-400">{page}</span>
          <button data-testid="inv-next" disabled={!hasMore} onClick={() => load(page + 1)}
            className="rounded-full border border-slate-200 px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] disabled:opacity-40 transition-colors">→</button>
        </div>
      )}
    </div>
  );
}
